<template>
	<div class="planner m-t-2">
		<div class="container">

			<div class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ ariaSummary }}</div>

			<transition-group name="fade-items">
				<div v-for="(dayPart, dayPartIndex) in day.dayParts" :key="day.date + dayPartIndex">
					<div v-if="dayPart.startTimes.length > 0" class="planner-daypart" :class="'planner-daypart-'+dayPart.name.toLowerCase()">
						<div class="planner-daypart-leadin">
							Starts at
						</div>
						<div class="planner-daypart-label">
							{{ dayPart.name }}
						</div>
					</div>

					<transition-group name="fade-items">
						<div class="planner-times" data-testid="activityColumn" v-for="(startTime, startTimeIndex) in dayPart.startTimes" :key="day.date + dayPartIndex + startTimeIndex">
							<div class="planner-time">
								{{ startTime.time.split(' ')[0] }}<span class="time-ampm"> {{ startTime.time.split(' ')[1] }}</span>
							</div>

							<transition-group name="fade-items">
								<div class="planner-entry" v-for="(activity, activityIndex) in startTime.activities" :key="day.date + dayPartIndex + startTimeIndex + activityIndex" :class="{ 'card-danger': activity.isCanceled }">
									<div class="planner-row">
										<div class="planner-col-md-3">
											<div class="planner-entry-title">
												<span class="text-danger" v-if="activity.isCanceled">Canceled </span>
												<span v-if="activity.isCanceled"><s>{{ activity.name }}</s></span>
												<span v-else>{{ activity.name }}</span>
											</div>
										</div>
										<div class="planner-col-md-2 font-weight-bold">
											{{ activity.duration }}
										</div>
										<div class="planner-col-md-2">
											<span v-if="activity.instructors && activity.instructors.length > 0" v-html="buildInstructors(activity.instructors)"></span>
										</div>
										<div class="planner-col-md-3">
											{{ activity.location }}
										</div>
										<div class="planner-entry-cta planner-col-md-2">
											<a :href="noSessions ? null : reserveUrl(activity, startTime.start)" v-if="!activity.isCanceled && startTime.timestamp > dayFromNow" class="btn btn-xs btn-primary" :class="{'disabled': noSessions}" :tabindex="noSessions ? -1 : null" :aria-disabled="noSessions" data-testid="reserveBtn">Reserve</a>
										</div>
									</div>
								</div>
							</transition-group>

						</div>
					</transition-group>
				</div>
			</transition-group>

			<div v-if="day && day.total" class="disclaimer">Appointment availability is subject to change.</div>

		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue';
import { Helpers } from '@/common/helpers';
import { clockObservable$, dateProvider } from '@/services/clock.service';
import { FrameworkService } from '@/services/framework.service';

let clockSubscription;

export default defineComponent({
	name: 'ScheduleSub',
	props: {
		day: {
			default: null,
			type: Object
		},
		selectedServiceSessions: {
			type: Number,
			default: 0
		},
		selectedServiceActivityIds: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			dayFromNow: dateProvider() + 86400000, // 24 hours from now (in milliseconds)
			noSessions: false
		};
	},
	computed: {
		dayText() {
			const selectedDayMoment = Helpers.parseDateToMoment(this.day.day);
			return selectedDayMoment.format('dddd, MMMM D');
		},
		ariaSummary() {
			if (this.day.total) {
				return `${this.day.total} available time slots for ${this.dayText}`;
			}
			return 'Loading class schedules';
		}
	},
	created() {
		clockSubscription = clockObservable$
			.subscribe(m => {
				this.dayFromNow = m + 86400000; // 24 hours from now (in milliseconds)
			});
	},
	mounted() {
		this.completeMounting();

		// Override overflow: hidden on member site to prevent multiselects getting cut off at footer
		if (document.getElementsByClassName('main-mylt').length > 0) {
			document.getElementsByClassName('main-mylt')[0].style.overflow = 'visible';
		}
	},
	destroyed() {
		clockSubscription.unsubscribe();
	},
	methods: {
		completeMounting() {
			if (!(this.selectedServiceSessions > 0)) {
				this.noSessions = true;
			}
		},
		buildInstructors(instructors) {
			return Helpers.buildInstructors(instructors);
		},
		reserveUrl(activity, startTime) {
			let reserveUrl = activity.type === 'resource' ? FrameworkService.reserveResourceUrl(activity.id, this.selectedServiceActivityIds, startTime) : FrameworkService.reserveUrl(activity.id);
			return FrameworkService.addCancelUrl(reserveUrl);
		}
	}
});
</script>

<style scoped lang="scss">
.planner-daypart {
	top: 0;
}
.planner-time {
	top: 2rem;
}
.disclaimer {
	text-align: center;
	font-size: 0.75rem;
	padding: ($spacer - 0.25rem) 0 ($spacer - 1.25rem);
}
</style>
